import loadable from '@loadable/component';
import { addDays, startOfToday } from 'date-fns';
import * as React from 'react';
import { RangeKeyDict } from 'react-date-range';

import { CalendarLoadFallback } from './CalendarLoadFallback';
import { getIsResetButtonShow } from './utils';
import { LoadableErrorBoundary } from '../../../../packages/LoadableHelpers';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';

export interface IDateRangeModalProps {
  initialFrom: Date | undefined;
  initialTo: Date | undefined;
  min: Date;
  max: Date;
  testId?: string;
  onApply?(): void;
  onReset?(): void;
  onChange(from: Date | undefined, to: Date | undefined): void;
}

export const DateRangeModalContentLoadable = loadable(() => import('./DateRangeModalContent'), {
  fallback: <CalendarLoadFallback />,
  ssr: false,
});

export const DateRangeModal: React.FC<IDateRangeModalProps> = props => {
  const { logger } = useApplicationContext();
  const { initialFrom, initialTo, min, max, onApply, onChange, onReset } = props;

  const today = startOfToday();
  const initialFromValue = initialFrom || today;
  const initialToValue = initialTo || addDays(initialFromValue, 1);

  const [from, setFromDate] = React.useState<Date | undefined>(initialFromValue);
  const [to, setToDate] = React.useState<Date | undefined>(initialToValue);

  const isApplyButtonDisabled = !from;
  const isResetButtonShow = React.useMemo(() => {
    return getIsResetButtonShow({
      initialFrom: initialFromValue,
      initialTo: initialToValue,
      from: from!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
      to: to!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
    });
  }, [from, to]);

  const selection = {
    startDate: from,
    endDate: to,
    key: 'selection',
  };

  const handleRetryLoadContent = React.useCallback(() => {
    DateRangeModalContentLoadable.load();
  }, []);

  const handleChange = React.useCallback(
    (data: RangeKeyDict) => {
      const { startDate, endDate } = data.selection;

      setFromDate(startDate);
      setToDate(endDate);
    },
    [setFromDate, setToDate],
  );

  const handleResetClick = React.useCallback(() => {
    setFromDate(initialFromValue);
    setToDate(initialToValue);
    onReset?.();
  }, [setFromDate, setToDate, onReset]);

  const handleApplyClick = React.useCallback(() => {
    const fromValue = from!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const toValue = to || addDays(fromValue, 1);

    if (!to) {
      setToDate(toValue);
    }

    onChange(fromValue, toValue);
    if (onApply) {
      onApply();
    }
  }, [from, to]);

  return (
    <LoadableErrorBoundary logger={logger} onRetry={handleRetryLoadContent}>
      <DateRangeModalContentLoadable
        min={min}
        max={max}
        applyButtonDisabled={isApplyButtonDisabled}
        showResetButton={isResetButtonShow}
        selection={selection}
        onChange={handleChange}
        onApplyClick={handleApplyClick}
        onResetClick={handleResetClick}
      />
    </LoadableErrorBoundary>
  );
};
